import { Injectable } from '@angular/core';

export type ScreenshotCaptureResult = {
  blob: Blob;
  objectUrl: string;
  width: number;
  height: number;
  contentType: string;
};

@Injectable({
  providedIn: 'root',
})
export class ScreenshotService {
  async captureScreen(): Promise<ScreenshotCaptureResult | null> {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return null;
    }

    const getDisplayMedia = navigator.mediaDevices?.getDisplayMedia?.bind(navigator.mediaDevices);
    if (!getDisplayMedia) {
      return null;
    }

    let stream: MediaStream | null = null;
    try {
      stream = await getDisplayMedia({ video: true, audio: false } as MediaStreamConstraints);

      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;

      await new Promise<void>((resolve) => {
        video.onloadedmetadata = () => resolve();
      });

      await video.play().catch(() => {});

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 0;
      canvas.height = video.videoHeight || 0;

      const ctx = canvas.getContext('2d');
      if (!ctx || !canvas.width || !canvas.height) {
        return null;
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png', 0.92));
      if (!blob) {
        return null;
      }

      return {
        blob,
        objectUrl: URL.createObjectURL(blob),
        width: canvas.width,
        height: canvas.height,
        contentType: blob.type || 'image/png',
      };
    } catch {
      return null;
    } finally {
      stream?.getTracks().forEach((track) => track.stop());
    }
  }

  revokeObjectUrl(url: string | null | undefined): void {
    if (!url || typeof URL === 'undefined') {
      return;
    }

    URL.revokeObjectURL(url);
  }
}

